import { Dialog, OdinIcon } from '@myosh/odin-components'
import { useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useDeleteTrainingMatrixReportMutation } from '../../../../API/rtk-query-api/store/tmsApi'
import ActionIcon, { type ActionIconProps } from '../../../UI/action-icon/action-icon'
import LoadingSpinner from '../../../UI/loading-spinner'
import { Page } from '../../training-matrix/view_dropdown_data'
import ScheduledExport from './ScheduledReport'
import './scheduleExport.scss'
import {
  useGapAnalysisScheduledReportsQuery,
  useTrainingMatrixScheduledReportsQuery
} from '../../../../API/rtk-query-api/store/customApi'
import Notifications from '../../../UI/notifications/Notifications'

const dialogId = 'scheduled-report-list-dialog'
const scheduledReportListToasterId = 'scheduled-report-list-toaster'

interface ScheduledReportListProps {
  selectedPage: Page
  hide: () => void
}

function ScheduledReportList({ selectedPage, hide }: ScheduledReportListProps) {
  const [showReportModal, setShowReportModal] = useState(false)
  const [reportToDeleteID, setReportToDeleteID] = useState<string>()
  const reportEditID = useRef<string | undefined>(undefined)

  const { data: gapAnalysisReports, isFetching: isFetchingGapAnalysisReports } = useGapAnalysisScheduledReportsQuery(
    {},
    { skip: selectedPage !== Page.GapAnalysis }
  )
  const { data: trainingMatrixReports, isFetching: isFetchingTrainingMatrixReports } =
    useTrainingMatrixScheduledReportsQuery({}, { skip: selectedPage !== Page.TrainingMatrix })
  const [deleteReport] = useDeleteTrainingMatrixReportMutation()

  const onHideModal = () => {
    setReportToDeleteID(undefined)
    hide()
  }

  const handleShowReportModal = () => setShowReportModal(true)
  const handleCloseReportModal = () => {
    setShowReportModal(false)
    if (reportEditID.current) {
      reportEditID.current = undefined
    }
  }
  const onEditReportClick = (reportId: string | undefined) => {
    setShowReportModal(true)
    if (reportId) {
      reportEditID.current = reportId
    }
  }

  const onDeleteReport = (reportId: string | undefined) => {
    if (reportId) {
      toast.promise(
        deleteReport({ id: reportId })
          .unwrap()
          .then(() => {
            setReportToDeleteID(undefined)
          }),
        {
          loading: 'Deleting Report...',
          success: 'Report Deleted',
          error: 'Failed To Delete Report'
        },
        { toasterId: scheduledReportListToasterId }
      )
    }
  }

  const reports = useMemo(() => {
    if (selectedPage === Page.GapAnalysis) {
      return gapAnalysisReports
    } else if (selectedPage === Page.TrainingMatrix) {
      return trainingMatrixReports
    }
  }, [gapAnalysisReports, trainingMatrixReports, selectedPage])

  const isFetching = isFetchingGapAnalysisReports || isFetchingTrainingMatrixReports

  return (
    <>
      <Dialog id={dialogId} header='Scheduled Exports' visible onClose={onHideModal}>
        <button
          onClick={handleShowReportModal}
          className='icon-text-btn p-button'
          aria-label='New Report'
          type='button'>
          <OdinIcon icon='Plus' />
          <span>New Report</span>
        </button>
        <div className='schedule-report-list'>
          {isFetching && (
            <div style={{ display: 'grid', placeItems: 'center' }}>
              <LoadingSpinner />
            </div>
          )}
          {!isFetching &&
            reports?.data?.map((report, index) => {
              const isEven = index % 2 === 1
              if (reportToDeleteID === report.id) {
                return (
                  <ScheduleReportListItem
                    title={`Delete "${report.name}"?`}
                    actionItems={[
                      { icon: 'Check', onClick: () => onDeleteReport(report.id), iconTheme: 'default' },
                      { icon: 'X', onClick: () => setReportToDeleteID(undefined), iconTheme: 'default' }
                    ]}
                    className='delete-confirmation-item'
                    key={report.id}
                  />
                )
              }

              return (
                <ScheduleReportListItem
                  title={report.name}
                  actionItems={[
                    { icon: 'Pencil', onClick: () => onEditReportClick(report.id) },
                    { icon: 'Trash', onClick: () => setReportToDeleteID(report.id) }
                  ]}
                  isEven={isEven}
                  key={report.id}
                />
              )
            })}
        </div>
        <Notifications
          appendTo={document.getElementById(dialogId) ?? document.body}
          toasterId={scheduledReportListToasterId}
        />
      </Dialog>
      <ScheduledExport
        isVisible={showReportModal}
        id={reportEditID.current}
        selectedPage={selectedPage}
        hide={handleCloseReportModal}
      />
    </>
  )
}

export default ScheduledReportList

interface ScheduleReportListItemProps {
  title: string
  actionItems: Array<ActionIconProps>
  isEven?: boolean
  className?: string
}

function ScheduleReportListItem({ title, actionItems, isEven, className }: ScheduleReportListItemProps) {
  const defaultClass = 'schedule-report-list__item'
  const itemClass = className ? `${defaultClass} ${className}` : defaultClass

  return (
    <div className={itemClass} data-even={isEven}>
      <p title={title}>{title}</p>
      <div className='schedule-report-list__item-actions'>
        {actionItems.map(item => {
          return <ActionIcon {...item} key={item.icon} />
        })}
      </div>
    </div>
  )
}
